import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { combineLatest, distinctUntilChanged, finalize, map, Subscription } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { EmptyStateComponent } from '../../../shared/ui/empty-state/empty-state.component';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import {
  DataViewShellComponent,
  ErrorBlockComponent,
  FloatingActionFabComponent,
  ListFilterShortcutDirective,
  PageActionModalComponent,
  PageContextGuardComponent,
  PageShellComponent,
  QuickViewModalShellComponent,
} from '@clinic-platform/ui-components';
import {
  NotificationTemplateEventViewDto,
  NotificationTemplateVariableDescriptorDto,
  NotificationTemplateVariablesResponseDto,
  NotificationTemplatesListResponseDto,
} from '../data-access/notification-templates.models';
import { NotificationTemplatesService } from '../data-access/notification-templates.service';

type ScopeMode = 'global' | 'company';
type CreateTemplateMode = 'event' | 'override';
type TemplateChannelFilter = '' | 'email' | 'sms' | 'email_sms' | 'missing';
type TemplateSourceFilter = '' | NotificationTemplateEventViewDto['source'];
type TemplateSort = 'eventAsc' | 'eventDesc' | 'updatedDesc' | 'updatedAsc';
type TemplateFilterState = {
  eventQuery: string;
  channel: TemplateChannelFilter;
  source: TemplateSourceFilter;
  sort: TemplateSort;
};

@Component({
  selector: 'app-notification-templates-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    EmptyStateComponent,
    CompanyContextRequiredComponent,
    DataViewShellComponent,
    FloatingActionFabComponent,
    ErrorBlockComponent,
    ListFilterShortcutDirective,
    PageActionModalComponent,
    PageContextGuardComponent,
    PageShellComponent,
    QuickViewModalShellComponent,
  ],
  template: `
    <lib-page-shell
      libListFilterShortcut
      [libListFilterShortcut]="!(scopeMode === 'company' && !effectiveCompanyId)"
      (libListFilterShortcutOpen)="openFilterModal()"
    >
      @if (isSystemAdmin) {
        <section class="templates-page__scope-card">
          <div class="templates-page__meta">
            Scope from header:
            <strong>{{ scopeMode === 'global' ? 'Global defaults' : 'Company override' }}</strong>
          </div>
          <div class="templates-page__meta">
            Company:
            <strong>{{ contextCompanyId || 'not selected' }}</strong>
          </div>
          <div class="templates-page__meta">
            Clinic:
            <strong>{{ contextClinicId || 'not selected' }}</strong>
          </div>
        </section>
      }

      <lib-page-context-guard [blocked]="scopeMode === 'company' && !effectiveCompanyId">
        <app-company-context-required />
      </lib-page-context-guard>

      @if (!(scopeMode === 'company' && !effectiveCompanyId)) {
        @if (actionErrorText) {
          <lib-error-block variant="error" [message]="(actionErrorText | t) ?? ''" />
        }
        @if (actionSuccessText) {
          <lib-error-block variant="success" [message]="actionSuccessText" />
        }

        <lib-data-view-shell
          [isLoading]="isLoading"
          [hasError]="!!errorText"
          [isEmpty]="!isLoading && !errorText && filteredEvents.length === 0"
        >
          <app-empty-state
            dataViewLoading
            title="Loading templates..."
            description="Please wait."
          />

          <app-empty-state
            dataViewError
            title="Failed to load templates"
            [description]="(errorText | t) ?? ''"
          />

          <app-empty-state
            dataViewEmpty
            title="No notification templates"
            description="No templates match current filters."
          />

          <section class="templates-page__table-shell" aria-label="Notification templates">
            <table class="templates-page__table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Channel</th>
                  <th>Inheritance</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                @for (item of filteredEvents; track item.eventType) {
                  <tr class="templates-page__row" (click)="openTemplateDetail(item)">
                    <td>
                      <strong>{{ humanizeKey(item.eventType) }}</strong>
                      <span>{{ item.eventType }}</span>
                    </td>
                    <td>
                      <span class="templates-page__pill">{{ getChannelLabel(item) }}</span>
                    </td>
                    <td>
                      <span class="templates-page__pill" [class.templates-page__pill--muted]="item.source === 'none'">
                        {{ getInheritanceLabel(item) }}
                      </span>
                    </td>
                    <td>{{ formatDateTime(item.updatedAt) }}</td>
                    <td>
                      <div class="templates-page__row-actions" (click)="$event.stopPropagation()">
                        @if (item.hasOwnTemplate) {
                          <button type="button" class="templates-page__action" (click)="startEdit(item)">
                            Edit
                          </button>
                          <button
                            type="button"
                            class="templates-page__action templates-page__action--danger"
                            [disabled]="isDeletingEventType === item.eventType"
                            (click)="deleteTemplate(item.eventType)"
                          >
                            {{ isDeletingEventType === item.eventType ? 'Deleting...' : 'Delete' }}
                          </button>
                          <button
                            type="button"
                            class="templates-page__action"
                            [disabled]="isTestingEventType === item.eventType"
                            (click)="sendTemplateTest(item)"
                          >
                            {{ isTestingEventType === item.eventType ? 'Sending...' : 'Test' }}
                          </button>
                        } @else if (scopeMode === 'company') {
                          <button type="button" class="templates-page__action" (click)="prefillCreate(item)">
                            Create override
                          </button>
                          @if (item.source === 'global') {
                            <button
                              type="button"
                              class="templates-page__action"
                              [disabled]="isTestingEventType === item.eventType"
                              (click)="sendTemplateTest(item)"
                            >
                              {{ isTestingEventType === item.eventType ? 'Sending...' : 'Test' }}
                            </button>
                          }
                        } @else {
                          <span class="templates-page__meta">No template</span>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </section>
        </lib-data-view-shell>

        <lib-floating-action-fab
          [menuTemplate]="templatesActionsMenu"
          buttonAriaLabel="Open notification templates actions"
        />

        <ng-template #templatesActionsMenu let-close="close">
          <div class="templates-page__actions-menu">
            <button
              type="button"
              role="menuitem"
              class="action-menu__item"
              [disabled]="availableEventsForCreate.length === 0"
              (click)="openCreateModal(); close()"
            >
              {{ scopeMode === 'company' ? 'Create override' : 'Create template' }}
            </button>
            <button
              type="button"
              role="menuitem"
              class="action-menu__item"
              (click)="openFilterModal(); close()"
            >
              Filters & Sort (Ctrl+F)
            </button>
            @if (hasActiveFilters) {
              <button
                type="button"
                role="menuitem"
                class="action-menu__item"
                (click)="onResetFilters(); close()"
              >
                Reset
              </button>
            }
            <button
              type="button"
              role="menuitem"
              class="action-menu__item"
              (click)="loadTemplates(); close()"
            >
              Refresh
            </button>
          </div>
        </ng-template>

        <lib-page-action-modal
          [isOpen]="isFilterModalOpen"
          title="Filters & Sort"
          subtitle="Notification template list controls"
          width="760px"
          [canSubmit]="true"
          (close)="closeFilterModal()"
          (submit)="onApplyFilters(); closeFilterModal()"
        >
          <form
            [formGroup]="filtersForm"
            class="templates-page__modal-filters"
            (submit)="$event.preventDefault(); onApplyFilters(); closeFilterModal()"
          >
            <div class="templates-page__filters-grid">
              <label>
                <span>Event</span>
                <input formControlName="eventQuery" placeholder="Search event" data-modal-primary-focus="true" />
              </label>

              <label>
                <span>Channel</span>
                <select formControlName="channel">
                  <option value="">All</option>
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="email_sms">Email + SMS</option>
                  <option value="missing">Missing template</option>
                </select>
              </label>

              <label>
                <span>Inheritance</span>
                <select formControlName="source">
                  <option value="">All</option>
                  <option value="global">Inherited / global</option>
                  <option value="company_override">Company override</option>
                  <option value="none">Missing</option>
                </select>
              </label>

              <label>
                <span>Sort</span>
                <select formControlName="sort">
                  <option value="eventAsc">Event A-Z</option>
                  <option value="eventDesc">Event Z-A</option>
                  <option value="updatedDesc">Recently updated</option>
                  <option value="updatedAsc">Oldest updated</option>
                </select>
              </label>
            </div>

            <div class="templates-page__modal-actions">
              <button type="button" class="templates-page__action" (click)="onResetFilters()">
                Reset
              </button>
              <button type="submit" class="templates-page__action templates-page__action--primary">
                Apply Filters
              </button>
            </div>
          </form>
        </lib-page-action-modal>

        <lib-page-action-modal
          [isOpen]="isCreateModalOpen"
          [title]="createTemplateMode === 'override' ? 'Create Override' : 'Create Template'"
          subtitle="Notification template content"
          width="920px"
          [canClose]="!isSavingCreate"
          [canSubmit]="!isSavingCreate && createForm.valid"
          (close)="closeCreateModal()"
          (submit)="createTemplate()"
        >
          <form [formGroup]="createForm" (ngSubmit)="createTemplate()" class="templates-page__form-grid">
            @if (createTemplateMode === 'event') {
              <label>
                <span>Event Type</span>
                <select
                  formControlName="eventType"
                  data-modal-primary-focus="true"
                  (change)="onCreateEventTypeChange()"
                >
                  <option value="">Select event</option>
                  @for (eventType of availableEventsForCreate; track eventType) {
                    <option [value]="eventType">{{ humanizeKey(eventType) }}</option>
                  }
                </select>
              </label>
            } @else {
              <div class="templates-page__field-card templates-page__wide">
                <span>Event Type</span>
                <strong>{{ humanizeKey(createForm.controls.eventType.value) }}</strong>
                <div>{{ createForm.controls.eventType.value || '—' }}</div>
              </div>
            }

            <label class="templates-page__wide">
              <span>Full Message</span>
              <textarea formControlName="fullMessage" rows="5"></textarea>
            </label>

            <label class="templates-page__wide">
              <span>Short Message (SMS)</span>
              <textarea formControlName="shortMessage" rows="3"></textarea>
            </label>

            <label class="templates-page__checkbox templates-page__wide">
              <input type="checkbox" formControlName="calendarInviteEnabled" />
              <span>Attach calendar invite (.ics)</span>
            </label>

            <div class="templates-page__variables-box templates-page__wide">
              <h4>Available Variables</h4>
              @if (selectedEventVariables.length === 0) {
                <div class="templates-page__meta">Select an event to see available variables.</div>
              } @else {
                <div class="templates-page__variables-list">
                  @for (variable of selectedEventVariables; track variable.key) {
                    <div class="templates-page__variable-item">
                      <code>{{ '{{' }}{{ variable.key }}{{ '}}' }}</code>
                      <span>{{ variable.description }}</span>
                    </div>
                  }
                </div>
              }
            </div>

            <div class="templates-page__modal-actions">
              <button type="button" class="templates-page__action" [disabled]="isSavingCreate" (click)="closeCreateModal()">
                Cancel
              </button>
              <button type="submit" class="templates-page__action templates-page__action--primary" [disabled]="createForm.invalid || isSavingCreate">
                {{ isSavingCreate ? 'Creating...' : 'Create template' }}
              </button>
            </div>
          </form>
        </lib-page-action-modal>

        <lib-page-action-modal
          [isOpen]="isEditModalOpen && !!editingEventType"
          title="Edit Template"
          [subtitle]="editingEventType ? humanizeKey(editingEventType) : ''"
          width="920px"
          [canClose]="!isSavingEdit"
          [canSubmit]="!isSavingEdit && editForm.valid"
          (close)="closeEditModal()"
          (submit)="saveEditingTemplate()"
        >
          <form [formGroup]="editForm" (ngSubmit)="saveEditingTemplate()" class="templates-page__form-grid">
            <label class="templates-page__wide">
              <span>Full Message</span>
              <textarea [formControl]="editForm.controls.fullMessage" data-modal-primary-focus="true" rows="8"></textarea>
            </label>

            <label class="templates-page__wide">
              <span>Short Message (SMS)</span>
              <textarea [formControl]="editForm.controls.shortMessage" rows="4"></textarea>
            </label>

            <label class="templates-page__checkbox templates-page__wide">
              <input type="checkbox" [formControl]="editForm.controls.calendarInviteEnabled" />
              <span>Attach calendar invite (.ics)</span>
            </label>

            <div class="templates-page__variables-box templates-page__wide">
              <h4>Available Variables</h4>
              @if (editingEventVariables.length === 0) {
                <div class="templates-page__meta">No variables registered for this event.</div>
              } @else {
                <div class="templates-page__variables-list">
                  @for (variable of editingEventVariables; track variable.key) {
                    <div class="templates-page__variable-item">
                      <code>{{ '{{' }}{{ variable.key }}{{ '}}' }}</code>
                      <span>{{ variable.description }}</span>
                    </div>
                  }
                </div>
              }
            </div>

            <div class="templates-page__modal-actions">
              <button type="button" class="templates-page__action" [disabled]="isSavingEdit" (click)="closeEditModal()">
                Cancel
              </button>
              <button type="submit" class="templates-page__action templates-page__action--primary" [disabled]="editForm.invalid || isSavingEdit">
                {{ isSavingEdit ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </form>
        </lib-page-action-modal>

        <lib-quick-view-modal-shell
          [isOpen]="!!viewingTemplate"
          title="Template Quick View"
          [subtitle]="viewingTemplate ? humanizeKey(viewingTemplate.eventType) : ''"
          width="920px"
          (close)="closeTemplateDetail()"
        >
          @if (viewingTemplate; as item) {
            <div class="quick-view-grid">
              <div class="quick-view-field">
                <span>Event</span>
                <strong>{{ humanizeKey(item.eventType) }}</strong>
                <div class="quick-view-id-meta">{{ item.eventType }}</div>
              </div>

              <div class="quick-view-field">
                <span>Channel</span>
                <strong>{{ getChannelLabel(item) }}</strong>
              </div>

              <div class="quick-view-field">
                <span>Inheritance</span>
                <strong>{{ getInheritanceLabel(item) }}</strong>
              </div>

              <div class="quick-view-field">
                <span>Calendar Invite</span>
                <strong>{{ item.calendarInviteEnabled ? 'Enabled' : 'Disabled' }}</strong>
              </div>

              <div class="quick-view-field">
                <span>Updated</span>
                <strong>{{ formatDateTime(item.updatedAt) }}</strong>
              </div>

              <div class="quick-view-field quick-view-field--wide">
                <span>Full Message</span>
                <pre class="templates-page__message" [innerHTML]="highlightTemplateVariables(item.fullMessage)"></pre>
              </div>

              <div class="quick-view-field quick-view-field--wide">
                <span>Short Message (SMS)</span>
                <pre class="templates-page__message" [innerHTML]="highlightTemplateVariables(item.shortMessage)"></pre>
              </div>

              <div class="quick-view-field quick-view-field--wide">
                <span>Available Variables</span>
                @if (getVariablesForEvent(item.eventType).length === 0) {
                  <strong>—</strong>
                } @else {
                  <div class="templates-page__variables-list">
                    @for (variable of getVariablesForEvent(item.eventType); track variable.key) {
                      <div class="templates-page__variable-item">
                        <code>{{ '{{' }}{{ variable.key }}{{ '}}' }}</code>
                        <span>{{ variable.description }}</span>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }
        </lib-quick-view-modal-shell>
      }
    </lib-page-shell>
  `,
  styles: [`
    .templates-page__card {
      margin-bottom: 16px;
      padding: 16px;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: #ffffff;
    }

    .templates-page__scope-card {
      margin-bottom: 16px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px 16px;
      align-items: center;
      padding: 12px 14px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: #ffffff;
    }

    .templates-page__table-shell {
      overflow-x: auto;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: #ffffff;
    }

    .templates-page__table {
      width: 100%;
      min-width: 900px;
      border-collapse: collapse;
    }

    .templates-page__table th,
    .templates-page__table td {
      padding: 13px 16px;
      border-bottom: 1px solid #edf0f4;
      text-align: left;
      vertical-align: middle;
      color: #111827;
      font-size: 14px;
    }

    .templates-page__table th {
      background: #f8fafc;
      color: #334155;
      font-size: 13px;
      font-weight: 700;
    }

    .templates-page__table td > span:not(.templates-page__pill) {
      display: block;
      margin-top: 3px;
      color: #64748b;
      font-size: 12px;
    }

    .templates-page__row {
      cursor: pointer;
    }

    .templates-page__row:hover {
      background: #f8fbff;
    }

    .templates-page__row-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
    }

    .templates-page__pill {
      display: inline-flex;
      align-items: center;
      min-height: 24px;
      margin: 0;
      border-radius: 999px;
      background: #eef2ff;
      color: #3730a3;
      padding: 2px 9px;
      font-size: 12px;
      font-weight: 700;
    }

    .templates-page__pill--muted {
      background: #f1f5f9;
      color: #64748b;
    }

    .templates-page__modal-filters,
    .templates-page__form-grid {
      display: grid;
      gap: 14px;
    }

    .templates-page__filters-grid,
    .templates-page__form-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .templates-page__filters-grid {
      display: grid;
      gap: 12px;
    }

    .templates-page__filters-grid label,
    .templates-page__form-grid label {
      display: grid;
      gap: 6px;
      min-width: 0;
      color: #334155;
      font-size: 13px;
      font-weight: 700;
    }

    .templates-page__checkbox {
      display: flex !important;
      grid-template-columns: none !important;
      align-items: center;
      gap: 8px;
      color: #111827;
      font-size: 14px;
      font-weight: 700;
    }

    .templates-page__checkbox input {
      width: 18px;
      height: 18px;
      margin: 0;
    }

    .templates-page__filters-grid input,
    .templates-page__filters-grid select,
    .templates-page__form-grid select,
    .templates-page__form-grid textarea {
      width: 100%;
      box-sizing: border-box;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background: #ffffff;
      color: #111827;
      font-size: 14px;
    }

    .templates-page__filters-grid input,
    .templates-page__filters-grid select,
    .templates-page__form-grid select {
      height: 40px;
      padding: 0 10px;
    }

    .templates-page__form-grid textarea {
      min-height: 110px;
      padding: 10px 12px;
      resize: vertical;
    }

    .templates-page__field-card {
      display: grid;
      gap: 5px;
      padding: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: #f8fafc;
      color: #64748b;
      font-size: 13px;
    }

    .templates-page__field-card span {
      color: #334155;
      font-weight: 700;
    }

    .templates-page__scope-row {
      display: flex;
      flex-wrap: wrap;
      gap: 10px 16px;
      align-items: center;
    }

    .templates-page__radio {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #111827;
    }

    .templates-page__scope-row label {
      display: grid;
      gap: 6px;
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    .templates-page__scope-context {
      align-items: flex-start;
      flex-direction: column;
      gap: 6px;
    }

    .templates-page__card-head {
      display: flex;
      justify-content: space-between;
      gap: 10px;
      align-items: center;
    }

    .templates-page__open-create {
      height: 36px;
      border: 1px solid #2563eb;
      border-radius: 8px;
      background: #ffffff;
      color: #2563eb;
      padding: 0 12px;
      cursor: pointer;
      font-size: 13px;
      font-weight: 600;
    }

    .templates-page__open-create:disabled {
      opacity: 0.6;
      cursor: default;
    }

    .templates-page__grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      align-items: end;
    }

    .templates-page__modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(17, 24, 39, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      z-index: 1000;
    }

    .templates-page__modal {
      width: min(980px, 100%);
      max-height: calc(100vh - 32px);
      overflow: auto;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 16px;
      box-sizing: border-box;
    }

    .templates-page__modal-title {
      margin: 0 0 12px;
      color: #111827;
      font-size: 18px;
      font-weight: 700;
    }

    .templates-page__modal-actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 4px;
    }

    .templates-page__wide {
      grid-column: 1 / -1;
    }

    .templates-page__grid label {
      display: grid;
      gap: 6px;
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    .templates-page__grid select,
    .templates-page__grid textarea,
    .templates-page__event-field textarea {
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 14px;
      box-sizing: border-box;
      width: 100%;
      resize: vertical;
    }

    .templates-page__grid select {
      height: 40px;
      padding: 0 10px;
    }

    .templates-page__grid button {
      height: 40px;
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #ffffff;
      padding: 0 14px;
      cursor: pointer;
    }

    .templates-page__grid button:disabled,
    .templates-page__action:disabled {
      opacity: 0.6;
      cursor: default;
    }

    .templates-page__event-panel {
      margin-top: 10px;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: #ffffff;
      padding: 12px;
      display: grid;
      gap: 12px;
    }

    .templates-page__event-header {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 16px;
      align-items: center;
    }

    .templates-page__event-field {
      display: grid;
      gap: 6px;
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    .templates-page__message {
      margin: 0;
      white-space: pre-wrap;
      color: #111827;
      font-family: inherit;
      min-height: 48px;
    }

    .templates-page__action {
      height: 32px;
      border-radius: 8px;
      border: 1px solid #2563eb;
      background: #ffffff;
      color: #2563eb;
      padding: 0 10px;
      font-size: 13px;
      cursor: pointer;
      margin: 0;
    }

    .templates-page__action--primary {
      background: #2563eb;
      color: #ffffff;
    }

    .templates-page__action--danger {
      border-color: #dc2626;
      color: #dc2626;
    }

    .templates-page__meta {
      color: #6b7280;
      font-size: 13px;
    }

    .templates-page__variables-box {
      margin-top: 12px;
      padding: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: #f9fafb;
      display: grid;
      gap: 8px;
    }

    .templates-page__variables-box h4 {
      margin: 0;
      color: #111827;
      font-size: 14px;
    }

    .templates-page__variables-list {
      display: grid;
      gap: 6px;
    }

    .templates-page__variable-item {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      font-size: 13px;
      color: #374151;
    }

    .templates-page__variable-item code {
      background: #eef2ff;
      border: 1px solid #c7d2fe;
      color: #3730a3;
      border-radius: 6px;
      padding: 2px 6px;
      font-size: 12px;
    }

    @media (max-width: 900px) {
      .templates-page__card-head {
        flex-direction: column;
        align-items: stretch;
      }

      .templates-page__open-create,
      .templates-page__modal-actions .templates-page__action,
      .templates-page__grid button {
        width: 100%;
      }

      .templates-page__grid {
        grid-template-columns: 1fr;
      }

      .templates-page__filters-grid,
      .templates-page__form-grid {
        grid-template-columns: 1fr;
      }

      .templates-page__modal-actions {
        flex-direction: column-reverse;
      }
    }

    @media (max-width: 640px) {
      .templates-page__card {
        padding: 12px;
      }

      .templates-page__scope-row {
        gap: 8px;
      }

      .templates-page__modal-backdrop {
        padding: 8px;
        align-items: end;
      }

      .templates-page__modal {
        width: 100%;
        max-height: min(92dvh, calc(100dvh - 12px));
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        padding: 12px;
      }

      .templates-page__event-panel {
        padding: 10px;
      }

      .templates-page__event-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
      }

      .templates-page__action {
        width: 100%;
        margin-right: 0;
      }
    }
  `],
})
export class NotificationTemplatesPageComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly service = inject(NotificationTemplatesService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly subscriptions = new Subscription();

  protected readonly createForm = this.formBuilder.group({
    eventType: this.formBuilder.nonNullable.control('', [Validators.required]),
    fullMessage: this.formBuilder.nonNullable.control('', [Validators.required, Validators.maxLength(20000)]),
    shortMessage: this.formBuilder.nonNullable.control('', [Validators.required, Validators.maxLength(2000)]),
    calendarInviteEnabled: this.formBuilder.nonNullable.control(false),
  });

  protected readonly editForm = this.formBuilder.group({
    fullMessage: this.formBuilder.nonNullable.control('', [Validators.required, Validators.maxLength(20000)]),
    shortMessage: this.formBuilder.nonNullable.control('', [Validators.required, Validators.maxLength(2000)]),
    calendarInviteEnabled: this.formBuilder.nonNullable.control(false),
  });

  protected readonly filtersForm = this.formBuilder.group({
    eventQuery: this.formBuilder.nonNullable.control(''),
    channel: this.formBuilder.nonNullable.control<TemplateChannelFilter>(''),
    source: this.formBuilder.nonNullable.control<TemplateSourceFilter>(''),
    sort: this.formBuilder.nonNullable.control<TemplateSort>('eventAsc'),
  });

  protected isSystemAdmin = false;
  protected scopeMode: ScopeMode = 'company';
  protected selectedCompanyId = '';
  protected contextCompanyId = '';
  protected contextClinicId = '';

  protected isLoading = false;
  protected isSavingCreate = false;
  protected isSavingEdit = false;
  protected isDeletingEventType: string | null = null;
  protected isTestingEventType: string | null = null;

  protected errorText: string | null = null;
  protected actionErrorText: string | null = null;
  protected actionSuccessText: string | null = null;
  protected isCreateModalOpen = false;
  protected isEditModalOpen = false;
  protected isFilterModalOpen = false;
  protected createTemplateMode: CreateTemplateMode = 'event';

  protected events: NotificationTemplateEventViewDto[] = [];
  protected availableEventsForCreate: string[] = [];
  protected editingEventType: string | null = null;
  protected viewingTemplate: NotificationTemplateEventViewDto | null = null;
  protected appliedFilters: TemplateFilterState = this.getDefaultFilters();
  protected selectedEventVariables: NotificationTemplateVariableDescriptorDto[] = [];
  protected variablesByEvent = new Map<string, NotificationTemplateVariableDescriptorDto[]>();

  protected get filteredEvents(): NotificationTemplateEventViewDto[] {
    const query = this.appliedFilters.eventQuery.trim().toLocaleLowerCase();
    const filtered = this.events.filter((item) => {
      const matchesQuery = !query
        || item.eventType.toLocaleLowerCase().includes(query)
        || this.humanizeKey(item.eventType).toLocaleLowerCase().includes(query);
      const matchesChannel = this.matchesChannelFilter(item, this.appliedFilters.channel);
      const matchesSource = !this.appliedFilters.source
        || item.source === this.appliedFilters.source;

      return matchesQuery && matchesChannel && matchesSource;
    });

    return filtered.sort((left, right) => {
      switch (this.appliedFilters.sort) {
        case 'eventDesc':
          return right.eventType.localeCompare(left.eventType);
        case 'updatedDesc':
          return this.updatedTime(right) - this.updatedTime(left);
        case 'updatedAsc':
          return this.updatedTime(left) - this.updatedTime(right);
        case 'eventAsc':
        default:
          return left.eventType.localeCompare(right.eventType);
      }
    });
  }

  protected get hasActiveFilters(): boolean {
    const defaults = this.getDefaultFilters();
    return this.appliedFilters.eventQuery !== defaults.eventQuery
      || this.appliedFilters.channel !== defaults.channel
      || this.appliedFilters.source !== defaults.source
      || this.appliedFilters.sort !== defaults.sort;
  }

  protected get editingEventVariables(): NotificationTemplateVariableDescriptorDto[] {
    if (!this.editingEventType) {
      return [];
    }
    return this.variablesByEvent.get(this.editingEventType) ?? [];
  }

  protected get effectiveCompanyId(): string | undefined {
    if (this.scopeMode !== 'company') {
      return undefined;
    }

    if (this.isSystemAdmin) {
      return this.contextCompanyId.trim() || undefined;
    }

    return this.selectedCompanyId.trim() || undefined;
  }

  ngOnInit(): void {
    const role = this.authService.getCurrentRole();
    this.isSystemAdmin = role === 'system_admin';

    if (this.isSystemAdmin) {
      this.contextCompanyId = this.systemAdminContext.getCompanyId() ?? '';
      this.contextClinicId = this.systemAdminContext.getClinicId() ?? '';
      this.selectedCompanyId = this.contextCompanyId;
      this.scopeMode = this.systemAdminContext.isGlobalScope() ? 'global' : 'company';
      this.subscriptions.add(
        combineLatest([
          this.systemAdminContext.companyId$,
          this.systemAdminContext.scopeMode$,
        ]).pipe(
          map(([companyId, mode]): { companyId: string; mode: ScopeMode; key: string } => {
            const normalizedMode: ScopeMode = mode === 'global' ? 'global' : 'company';
            const normalizedCompanyId = companyId ?? '';

            return {
              companyId: normalizedCompanyId,
              mode: normalizedMode,
              key: normalizedMode === 'global' ? 'global' : `company:${normalizedCompanyId}`,
            };
          }),
          distinctUntilChanged((previous, current) => previous.key === current.key),
        ).subscribe(({ companyId, mode }) => {
          this.contextCompanyId = companyId;
          this.selectedCompanyId = this.contextCompanyId;
          this.scopeMode = mode;
          this.isCreateModalOpen = false;
          this.actionErrorText = null;
          this.actionSuccessText = null;
          this.cancelEdit();
          this.createForm.reset({
            eventType: '',
            fullMessage: '',
            shortMessage: '',
            calendarInviteEnabled: false,
          });

          if (this.scopeMode === 'global' || this.effectiveCompanyId) {
            this.loadTemplates();
            return;
          }

          this.clearTemplateState();
        }),
      );
      this.subscriptions.add(
        this.systemAdminContext.clinicId$.pipe(distinctUntilChanged()).subscribe((clinicId) => {
          this.contextClinicId = clinicId ?? '';
        }),
      );
    } else {
      this.scopeMode = 'company';
      this.selectedCompanyId = this.authService.getCurrentCompanyId() ?? '';
      this.loadTemplates();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected createTemplate(): void {
    if (this.createForm.invalid) {
      return;
    }

    if (this.scopeMode === 'company' && !this.effectiveCompanyId) {
      this.actionErrorText = 'error.generic';
      return;
    }

    const eventType = this.createForm.controls.eventType.value.trim();
    const fullMessage = this.createForm.controls.fullMessage.value.trim();
    const shortMessage = this.createForm.controls.shortMessage.value.trim();
    const calendarInviteEnabled = this.createForm.controls.calendarInviteEnabled.value;

    this.isSavingCreate = true;
    this.actionErrorText = null;
    this.actionSuccessText = null;

    this.service
      .create(
        {
          eventType,
          fullMessage,
          shortMessage,
          calendarInviteEnabled,
        },
        this.effectiveCompanyId,
      )
      .pipe(finalize(() => (this.isSavingCreate = false)))
      .subscribe({
        next: () => {
          this.actionSuccessText = 'Template created.';
          this.isCreateModalOpen = false;
          this.createTemplateMode = 'event';
          this.createForm.reset({
            eventType: '',
            fullMessage: '',
            shortMessage: '',
            calendarInviteEnabled: false,
          });
          this.selectedEventVariables = [];
          this.loadTemplates();
        },
        error: (error: unknown) => {
          this.actionErrorText = resolveHttpErrorMessageKey(error, {
            forbidden: 'error.forbiddenResource',
            fallback: 'error.generic',
          });
        },
      });
  }

  protected startEdit(item: NotificationTemplateEventViewDto): void {
    if (!item.hasOwnTemplate) {
      return;
    }

    this.editingEventType = item.eventType;
    this.isEditModalOpen = true;
    this.editForm.reset({
      fullMessage: item.fullMessage ?? '',
      shortMessage: item.shortMessage ?? '',
      calendarInviteEnabled: item.calendarInviteEnabled,
    });
  }

  protected cancelEdit(): void {
    if (this.isSavingEdit) {
      return;
    }
    this.editingEventType = null;
    this.isEditModalOpen = false;
    this.editForm.reset({
      fullMessage: '',
      shortMessage: '',
      calendarInviteEnabled: false,
    });
  }

  protected closeEditModal(): void {
    this.cancelEdit();
  }

  protected saveEditingTemplate(): void {
    if (!this.editingEventType) {
      return;
    }
    this.saveEdit(this.editingEventType);
  }

  protected saveEdit(eventType: string): void {
    if (this.editForm.invalid) {
      return;
    }

    this.isSavingEdit = true;
    this.actionErrorText = null;
    this.actionSuccessText = null;

    this.service
      .update(
        eventType,
        {
          fullMessage: this.editForm.controls.fullMessage.value.trim(),
          shortMessage: this.editForm.controls.shortMessage.value.trim(),
          calendarInviteEnabled: this.editForm.controls.calendarInviteEnabled.value,
        },
        this.effectiveCompanyId,
      )
      .pipe(finalize(() => (this.isSavingEdit = false)))
      .subscribe({
        next: () => {
          this.actionSuccessText = 'Template updated.';
          this.cancelEdit();
          this.loadTemplates();
        },
        error: (error: unknown) => {
          this.actionErrorText = resolveHttpErrorMessageKey(error, {
            forbidden: 'error.forbiddenResource',
            fallback: 'error.generic',
          });
        },
      });
  }

  protected deleteTemplate(eventType: string): void {
    if (!confirm(`Delete template for event "${eventType}"?`)) {
      return;
    }

    this.isDeletingEventType = eventType;
    this.actionErrorText = null;
    this.actionSuccessText = null;

    this.service
      .delete(eventType, this.effectiveCompanyId)
      .pipe(finalize(() => (this.isDeletingEventType = null)))
      .subscribe({
        next: () => {
          this.actionSuccessText = 'Template deleted.';
          if (this.editingEventType === eventType) {
            this.cancelEdit();
          }
          this.loadTemplates();
        },
        error: (error: unknown) => {
          this.actionErrorText = resolveHttpErrorMessageKey(error, {
            forbidden: 'error.forbiddenResource',
            fallback: 'error.generic',
          });
        },
      });
  }

  protected sendTemplateTest(item: NotificationTemplateEventViewDto): void {
    if (item.source === 'none' || !item.fullMessage) {
      this.actionErrorText = 'No effective template available for test send.';
      return;
    }

    const toEmailRaw = window.prompt(
      `Enter recipient email for test template "${item.eventType}"`,
      '',
    );

    if (toEmailRaw === null) {
      return;
    }

    const toEmail = toEmailRaw.trim();
    if (!toEmail) {
      return;
    }

    const companyIdForTest = this.scopeMode === 'company'
      ? this.effectiveCompanyId
      : (this.isSystemAdmin
        ? (this.contextCompanyId.trim() || undefined)
        : (this.selectedCompanyId.trim() || undefined));

    if (!companyIdForTest) {
      this.actionErrorText = 'error.settingsCompanyContextRequired';
      return;
    }

    this.isTestingEventType = item.eventType;
    this.actionErrorText = null;
    this.actionSuccessText = null;

    this.service
      .sendTemplateTest(item.eventType, toEmail, companyIdForTest)
      .pipe(finalize(() => (this.isTestingEventType = null)))
      .subscribe({
        next: () => {
          this.actionSuccessText = `Test email sent for "${item.eventType}".`;
        },
        error: (error: unknown) => {
          this.actionErrorText = resolveHttpErrorMessageKey(error, {
            forbidden: 'error.forbiddenResource',
            fallback: 'error.generic',
          });
        },
      });
  }

  protected prefillCreate(item: NotificationTemplateEventViewDto): void {
    if (!this.availableEventsForCreate.includes(item.eventType)) {
      return;
    }

    this.createForm.patchValue({
      eventType: item.eventType,
      fullMessage: item.fullMessage ?? '',
      shortMessage: item.shortMessage ?? '',
      calendarInviteEnabled: item.calendarInviteEnabled,
    });
    this.selectedEventVariables = this.variablesByEvent.get(item.eventType) ?? [];
    this.createTemplateMode = 'override';
    this.isCreateModalOpen = true;
    this.actionSuccessText = null;
    this.actionErrorText = null;
  }

  protected openCreateModal(): void {
    if (this.scopeMode === 'company' && !this.effectiveCompanyId) {
      return;
    }
    if (this.availableEventsForCreate.length === 0) {
      return;
    }
    this.createTemplateMode = 'event';
    this.createForm.reset({
      eventType: '',
      fullMessage: '',
      shortMessage: '',
      calendarInviteEnabled: false,
    });
    this.selectedEventVariables = [];
    this.isCreateModalOpen = true;
  }

  protected closeCreateModal(): void {
    if (this.isSavingCreate) {
      return;
    }
    this.createTemplateMode = 'event';
    this.isCreateModalOpen = false;
  }

  protected onCreateEventTypeChange(): void {
    const eventType = this.createForm.controls.eventType.value.trim();
    if (!eventType) {
      this.createForm.patchValue({
        fullMessage: '',
        shortMessage: '',
        calendarInviteEnabled: false,
      });
      this.selectedEventVariables = [];
      return;
    }

    const selected = this.events.find((item) => item.eventType === eventType);
    if (!selected) {
      return;
    }

    this.createForm.patchValue({
      fullMessage: selected.fullMessage ?? '',
      shortMessage: selected.shortMessage ?? '',
      calendarInviteEnabled: selected.calendarInviteEnabled,
    });
    this.selectedEventVariables = this.variablesByEvent.get(eventType) ?? [];
  }

  protected openFilterModal(): void {
    this.filtersForm.reset({ ...this.appliedFilters });
    this.isFilterModalOpen = true;
  }

  protected closeFilterModal(): void {
    this.isFilterModalOpen = false;
  }

  protected onApplyFilters(): void {
    const value = this.filtersForm.getRawValue();
    this.appliedFilters = {
      eventQuery: value.eventQuery.trim(),
      channel: value.channel,
      source: value.source,
      sort: value.sort,
    };
  }

  protected onResetFilters(): void {
    const defaults = this.getDefaultFilters();
    this.appliedFilters = defaults;
    this.filtersForm.reset({ ...defaults });
  }

  protected openTemplateDetail(item: NotificationTemplateEventViewDto): void {
    this.viewingTemplate = item;
  }

  protected closeTemplateDetail(): void {
    this.viewingTemplate = null;
  }

  protected highlightTemplateVariables(value: string | null | undefined): SafeHtml {
    const source = value ?? '';
    if (!source.trim()) {
      return '—';
    }

    const escaped = source
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const html = escaped.replace(
      /\{\{\s*[^{}]+\s*\}\}/g,
      (match) =>
        `<code style="background:#eef2ff;border:1px solid #c7d2fe;color:#3730a3;border-radius:6px;padding:1px 6px;font-size:12px;font-weight:600;font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,'Liberation Mono','Courier New',monospace;">${match}</code>`,
    );

    return this.domSanitizer.bypassSecurityTrustHtml(html);
  }

  protected humanizeKey(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    return normalized
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map((part) => `${part.charAt(0).toLocaleUpperCase()}${part.slice(1)}`)
      .join(' ');
  }

  protected getChannelLabel(item: NotificationTemplateEventViewDto): string {
    switch (this.getChannelKey(item)) {
      case 'email_sms':
        return 'Email + SMS';
      case 'email':
        return 'Email';
      case 'sms':
        return 'SMS';
      default:
        return 'Missing';
    }
  }

  protected getInheritanceLabel(item: NotificationTemplateEventViewDto): string {
    switch (item.source) {
      case 'company_override':
        return 'Company override';
      case 'global':
        return this.scopeMode === 'global' ? 'Global default' : 'Inherited global';
      default:
        return 'Missing';
    }
  }

  protected formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  }

  protected getVariablesForEvent(eventType: string): NotificationTemplateVariableDescriptorDto[] {
    return this.variablesByEvent.get(eventType) ?? [];
  }

  private getChannelKey(item: NotificationTemplateEventViewDto): TemplateChannelFilter {
    const hasFullMessage = !!item.fullMessage?.trim();
    const hasShortMessage = !!item.shortMessage?.trim();

    if (hasFullMessage && hasShortMessage) {
      return 'email_sms';
    }
    if (hasFullMessage) {
      return 'email';
    }
    if (hasShortMessage) {
      return 'sms';
    }
    return 'missing';
  }

  private matchesChannelFilter(
    item: NotificationTemplateEventViewDto,
    filter: TemplateChannelFilter,
  ): boolean {
    if (!filter) {
      return true;
    }

    const channel = this.getChannelKey(item);
    if (filter === 'email') {
      return channel === 'email' || channel === 'email_sms';
    }
    if (filter === 'sms') {
      return channel === 'sms' || channel === 'email_sms';
    }
    return channel === filter;
  }

  private getDefaultFilters(): TemplateFilterState {
    return {
      eventQuery: '',
      channel: '',
      source: '',
      sort: 'eventAsc',
    };
  }

  private updatedTime(item: NotificationTemplateEventViewDto): number {
    if (!item.updatedAt) {
      return 0;
    }
    const time = new Date(item.updatedAt).getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  protected loadTemplates(): void {
    if (this.scopeMode === 'company' && !this.effectiveCompanyId) {
      return;
    }

    this.isLoading = true;
    this.errorText = null;

    this.service
      .getList(this.effectiveCompanyId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: NotificationTemplatesListResponseDto) => {
          this.events = response.events;
          this.availableEventsForCreate = response.availableEventsForCreate;
          if (
            this.viewingTemplate
            && !this.events.some((item) => item.eventType === this.viewingTemplate?.eventType)
          ) {
            this.viewingTemplate = null;
          }

          if (
            this.createForm.controls.eventType.value &&
            !this.availableEventsForCreate.includes(this.createForm.controls.eventType.value)
          ) {
            this.createForm.patchValue({ eventType: '' });
            this.selectedEventVariables = [];
          }
          this.loadVariablesRegistry();
        },
        error: (error: unknown) => {
          this.errorText = resolveHttpErrorMessageKey(error, {
            forbidden: 'error.forbiddenResource',
            fallback: 'error.generic',
          });
        },
    });
  }

  private clearTemplateState(): void {
    this.events = [];
    this.availableEventsForCreate = [];
    this.selectedEventVariables = [];
    this.viewingTemplate = null;
    this.variablesByEvent.clear();
    this.errorText = null;
  }

  private loadVariablesRegistry(): void {
    this.service.getVariableRegistry(this.effectiveCompanyId).subscribe({
      next: (response: NotificationTemplateVariablesResponseDto) => {
        const map = new Map<string, NotificationTemplateVariableDescriptorDto[]>();
        for (const item of response.variablesByEvent ?? []) {
          map.set(item.eventType, item.variables);
        }
        this.variablesByEvent = map;
        this.onCreateEventTypeChange();
      },
      error: () => {
        this.variablesByEvent.clear();
        this.selectedEventVariables = [];
      },
    });
  }
}
